import { useEffect, useMemo } from "react";
import { useUser } from "./useUser";
import type { Nullable, NullableMaybe } from "types/utilities";
import { usePermission } from "@/features/auth/hooks/usePermission";
import { isAuthed } from "@/features/auth/models/authState";
import { useAuthStore } from "@/features/auth/stores/authStore";
import useLocale from "@/hooks/useLocale";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { isLocaleValue, type LocaleValue } from "@/utilities/locale";
import { usePreviousValue } from "@picklebet/ui";

export type AcceptOddsPreferences = "MANUAL" | "AUTO" | "AUTO_HIGHER";
export type OddsFormatPreferences = "DECIMAL" | "AMERICAN" | "FRACTIONAL";
export type DefaultHubPreferences = "CASINO" | "SPORTS" | "ESPORTS" | "RACING";

type Preferences = {
  acceptOdds: Nullable<AcceptOddsPreferences>;
  oddsFormat: Nullable<OddsFormatPreferences>;
  defaultHub: Nullable<DefaultHubPreferences>;
  allowCancelWithdrawal: Nullable<boolean>;
};

export const initialPreferences = {
  acceptOdds: null,
  oddsFormat: null,
  defaultHub: null,
  allowCancelWithdrawal: null,
} satisfies Preferences;

export const defaultPreferences = {
  acceptOdds: "AUTO_HIGHER",
  oddsFormat: "DECIMAL",
  defaultHub: "RACING",
  allowCancelWithdrawal: true,
} satisfies Preferences;

const defaultHubByLocale: Record<LocaleValue, DefaultHubPreferences> = {
  "en-au": "RACING",
  "en-ca": "SPORTS",
  "en-us": "SPORTS",
};

const homeUrlByHub: Record<DefaultHubPreferences, string> = {
  SPORTS: "/sports/betting",
  RACING: "/racing/betting/next-to-jump",
  ESPORTS: "/betting",
  CASINO: "/casino/lobby",
};

const hasAnyValue = (preferences: Preferences) =>
  Object.values(preferences).some((value) => value !== null);

const getLocaleDefaults = (locale: string | null): Preferences => ({
  ...defaultPreferences,
  defaultHub:
    locale && isLocaleValue(locale) ? defaultHubByLocale[locale] : "SPORTS",
});

const resolvePreferences = (
  localeDefaults: Preferences,
  userSettings: NullableMaybe<Partial<Preferences>>,
  canViewCasino: boolean,
): Preferences => {
  const { acceptOdds, oddsFormat, defaultHub, allowCancelWithdrawal } = {
    ...localeDefaults,
    ...userSettings,
  };
  const isCasinoBlocked = defaultHub === "CASINO" && !canViewCasino;

  return {
    acceptOdds,
    oddsFormat,
    defaultHub: isCasinoBlocked ? localeDefaults.defaultHub : defaultHub,
    allowCancelWithdrawal,
  };
};

const usePreferences = () => {
  const locale = useLocale();
  const previousLocale = usePreviousValue(locale);
  const casinoPermission = usePermission("viewCasino");
  const canViewCasino =
    casinoPermission !== undefined && casinoPermission !== "BLOCKED";

  const { user, isLoading: isUserLoading } = useUser();
  const userSettings = user?.settings;
  const isPermissionsLoading = useAuthStore(
    (state) =>
      state.status === "loading" ||
      Object.keys(state.session.permissions).length === 0,
  );
  const isResolving = isPermissionsLoading || isUserLoading;

  const [storedPreferences, setStoredPreferences, removeStoredPreferences] =
    useLocalStorage<Preferences>("userPreferences", initialPreferences);

  const localeDefaults = useMemo(() => getLocaleDefaults(locale), [locale]);

  const resolvedPreferences = useMemo(
    () =>
      isResolving
        ? null
        : resolvePreferences(localeDefaults, userSettings, canViewCasino),
    [isResolving, localeDefaults, userSettings, canViewCasino],
  );

  useEffect(() => {
    if (locale && previousLocale && locale !== previousLocale) {
      removeStoredPreferences();
    }
  }, [locale, previousLocale, removeStoredPreferences]);

  useEffect(() => {
    if (resolvedPreferences) setStoredPreferences(resolvedPreferences);
  }, [resolvedPreferences, setStoredPreferences]);

  const hasStoredPreferences = hasAnyValue(storedPreferences);
  const { acceptOdds, oddsFormat, defaultHub, allowCancelWithdrawal } =
    resolvedPreferences ??
    (hasStoredPreferences ? storedPreferences : localeDefaults);

  const preferences = useMemo(
    () => ({ acceptOdds, oddsFormat, defaultHub, allowCancelWithdrawal }),
    [acceptOdds, oddsFormat, defaultHub, allowCancelWithdrawal],
  );
  const isLoading = !resolvedPreferences && !hasStoredPreferences;

  return useMemo(() => ({ preferences, isLoading }), [preferences, isLoading]);
};

const useHomeUrl = () => {
  const { preferences, isLoading } = usePreferences();
  const isLoggedIn = useAuthStore(isAuthed);
  const isAuthLoading = useAuthStore((state) => state.status === "loading");
  const { defaultHub } = preferences;

  // casino must never be the homepage for logged-out visitors (crawlers
  // included), so wait for auth to settle before trusting isLoggedIn here
  if (isLoading || (defaultHub === "CASINO" && isAuthLoading)) {
    return { homeUrl: homeUrlByHub.SPORTS, isLoading: true };
  }

  if (!defaultHub || (defaultHub === "CASINO" && !isLoggedIn)) {
    return { homeUrl: homeUrlByHub.SPORTS, isLoading };
  }

  return { homeUrl: homeUrlByHub[defaultHub], isLoading };
};

export { usePreferences, type Preferences, useHomeUrl };
